import { createElement } from 'react';
import { Button, toast, type ToastOptions } from '@topolo-io/ui-kit';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

function toOptions(duration?: number, action?: ToastAction): number | ToastOptions | undefined {
  if (!action) return duration;
  return {
    ...(duration !== undefined ? { durationMs: duration } : {}),
    action: createElement(Button, {
      size: 'sm',
      variant: 'outline',
      onClick: action.onClick,
      children: action.label,
    }),
  };
}

function show(
  message: string,
  type: ToastType = 'info',
  duration?: number,
  action?: ToastAction
): string {
  const options = toOptions(duration, action);
  if (type === 'error') return toast.error(message, options);
  if (type === 'success') return toast.success(message, options);
  if (type === 'warning') return toast.warning(message, options);
  return toast.info(message, options);
}

export function useToast() {
  return {
    toasts: [],
    addToast: show,
    removeToast: toast.dismiss,
    clearAllToasts: toast.clear,
    showSuccess: (message: string, duration?: number, action?: ToastAction) =>
      show(message, 'success', duration, action),
    showError: (message: string, duration?: number, action?: ToastAction) =>
      show(message, 'error', duration, action),
    showWarning: (message: string, duration?: number, action?: ToastAction) =>
      show(message, 'warning', duration, action),
    showInfo: (message: string, duration?: number, action?: ToastAction) =>
      show(message, 'info', duration, action),
  };
}
